import { ReactElement, useMemo, useState } from 'react';
import { EmptyStateButton, IconButton, Info, Plus, Tooltip, Typography } from '@weareopenr/component-library';
import SectionModal from 'src/components/SectionModal';
import Divider from '../Divider';
import { AddProductsToMenuModal } from './AddProductsToMenuModal/AddProductsToMenuModal';
import MenuItemCardContainer from './MenuItemCardContainer';
import MenuItemSectionContainer from './MenuItemSectionContainer';
import { createCategoriesHierarchy, ICategoryWithChildren, isCategoryAssignedToMealtime } from './utilities';
import { IMenu } from 'src/types/menu.types';
import styles from './MenuSectionItems.module.scss';
import { CreateGlobalItemWizard } from '../CreateGlobalItemWizard/CreateGlobalItemWizard';

const MenuSectionItems = ({ menu }: { menu: IMenu }): ReactElement => {
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isSelectProductsModalOpen, setIsSelectProductsModalOpen] = useState<boolean>(false);
  const [sectionId, setSectionId] = useState<string | null>(null);
  const [parentSectionId, setParentSectionId] = useState<string | null>(null);
  const [isGlobalItemWizardOpen, setIsGlobalItemWizardOpen] = useState<boolean>(false);
  const { menuId, uncategorised, sections, modifiers, mealtimes } = menu;
  const categoriesHierachy = useMemo(() => createCategoriesHierarchy(sections), [sections, menu]);

  const showCreateSectionModal = (parentSectionId?: string | null): void => {
    if (parentSectionId !== undefined) setParentSectionId(parentSectionId);
    setIsModalVisible(true);
  };

  const showSelectProductsModal = (secId?: string): void => {
    setSectionId(secId ?? null);
    setIsSelectProductsModalOpen(true);
  };

  const closeCreateSectionModal = (): void => {
    setIsModalVisible(false);
  };

  return (
    <>
      {menu && (
        <>
          <SectionModal
            closeModal={closeCreateSectionModal}
            open={isModalVisible}
            menuId={menuId}
            parentSectionId={parentSectionId}
          />
          <AddProductsToMenuModal
            open={isSelectProductsModalOpen}
            closeModal={() => setIsSelectProductsModalOpen(false)}
            menuName={menu.name}
            menuId={menuId}
            sectionId={sectionId}
            onCreateNewProduct={() => setIsGlobalItemWizardOpen(true)}
            menuRanges={menu.ranges}
          />
          <CreateGlobalItemWizard
            open={isGlobalItemWizardOpen}
            closeModal={() => setIsGlobalItemWizardOpen(false)}
            menuId={menuId}
            sectionId={sectionId}
          />
          {(uncategorised.length > 0 || sections.length === 0) && (
            <>
              <Divider
                action={
                  <IconButton
                    buttonStyle="ghost"
                    icon={Plus}
                    onClick={() => showCreateSectionModal(null)}
                    aria-label="Add menu category"
                  />
                }
                title="Uncategorised"
              />
              <MenuItemCardContainer
                items={uncategorised}
                menuId={menuId}
                newItemButtonTitle="Add products"
                handleNewItemClick={() => showSelectProductsModal()}
              />
            </>
          )}
          {categoriesHierachy.map((category: ICategoryWithChildren, index: number) => (
            <MenuItemSectionContainer
              key={category.sectionId}
              category={category}
              menuId={menuId}
              onClickNewItem={showSelectProductsModal}
              handleSectionModal={showCreateSectionModal}
              upDisplayOrder={categoriesHierachy[index - 1]?.displayOrder}
              downDisplayOrder={categoriesHierachy[index + 1]?.displayOrder}
              isAssignedToMealtime={
                !mealtimes.length ? undefined : isCategoryAssignedToMealtime(category.sectionId, mealtimes)
              }
              tooltipPosition={!uncategorised.length && index === 0 ? 'bottom' : 'top'}
              sections={sections}
            />
          ))}
          <div className={styles['add-new-category']}>
            <EmptyStateButton label="+ Add menu category" onClick={() => showCreateSectionModal(null)} />
          </div>
          {modifiers.length > 0 && (
            <>
              <Divider title="Modifiers">
                <Typography size="medium" weight="bold">
                  Modifiers
                </Typography>
                <div className={styles['info-tooltip-logo-wrapper']}>
                  <Tooltip text="Modifiers are saved and editable but won’t be published in the menu as separate products.">
                    <Info />
                  </Tooltip>
                </div>
              </Divider>
              {modifiers?.length > 0 && (
                <MenuItemCardContainer
                  items={modifiers}
                  menuId={menuId}
                  newItemButtonTitle="New modifier"
                  handleNewItemClick={() => showSelectProductsModal()}
                />
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

export default MenuSectionItems;
